import { redactShopifyInstallTelemetry } from './shopifyInstallTelemetry';
import { setApiSentryReporter } from '@common/api/sentryReporter';
import { setApiTimingReporter, type ApiTimingReport } from '@common/api/rumReporter';
import * as Sentry from '@sentry/react';
import React from 'react';
import { preserveDiagnosticOccurrences } from './sentryDiagnosticDedupe';
import {
    SENTRY_FAILED_REQUEST_STATUS_CODES,
    shouldReportApiErrorToSentry,
} from './sentryApiFiltering';
import {
    createRoutesFromChildren,
    matchRoutes,
    useLocation,
    useNavigationType,
} from 'react-router-dom';

const DEFAULT_SENTRY_DSN = 'https://cb075024ca074f96db333747e4c4e17c@o4511115803557888.ingest.us.sentry.io/4511381628256256';
const SLOW_API_THRESHOLD_MS = 2_000;

type LogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal';

type SentryUserContext = {
    id?: string | null;
    email?: string | null;
    username?: string | null;
    workspaceId?: string | null;
    workspaceRole?: string | null;
};

let sentryInitialized = false;

const warnSentryFailure = (operation: string, error: unknown) => {
    if (import.meta.env.DEV) {
        console.warn(`[Sentry] ${operation} failed`, error);
    }
};

const safeRun = <T,>(operation: string, callback: () => T, fallback: T): T => {
    try {
        return callback();
    } catch (error) {
        warnSentryFailure(operation, error);
        return fallback;
    }
};

const getSentryDsn = () => {
    return import.meta.env.VITE_SENTRY_DSN || DEFAULT_SENTRY_DSN;
};

const getSentryRelease = (): string | undefined => {
    if (typeof __SENTRY_RELEASE__ === 'undefined') {
        return undefined;
    }

    return __SENTRY_RELEASE__ || undefined;
};

const getRouteContext = (): Record<string, unknown> => {
    if (typeof window === 'undefined') {
        return {};
    }

    return {
        route: window.location.pathname,
        route_search: window.location.search ? '[present]' : '',
    };
};

const sanitizeUrl = (value?: string): string | undefined => {
    if (!value) {
        return value;
    }

    try {
        const url = new URL(value, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
        return `${url.origin}${url.pathname}`;
    } catch {
        return value.split('?')[0];
    }
};

const normalizeError = (error: unknown): Error => {
    if (error instanceof Error) {
        return error;
    }

    if (typeof error === 'string') {
        return new Error(error);
    }

    try {
        return new Error(JSON.stringify(error));
    } catch {
        return new Error('Unknown error');
    }
};

const normalizeAttributes = (attributes: Record<string, unknown> = {}): Record<string, unknown> => ({
    app: 'website',
    environment: import.meta.env.MODE,
    ...getRouteContext(),
    ...attributes,
});

const buildApiTimingAttributes = (report: ApiTimingReport): Record<string, unknown> => {
    const attributes: Record<string, unknown> = {
        event: 'api_timing',
        area: 'rum',
        'api.kind': report.kind || 'http',
        'api.method': report.method,
        'api.route': report.route,
        'api.status': report.status,
        'api.duration_ms': report.durationMs,
        'api.timeout': report.timeout,
        'api.ok': report.ok,
    };

    if (report.kind === 'stream') {
        attributes['stream.first_chunk_ms'] = report.firstChunkMs;
        attributes['stream.message_count'] = report.messageCount;
        attributes['stream.aborted'] = report.aborted;
        attributes['stream.error_type'] = report.errorType;
    }

    return attributes;
};

export const initSentry = () => {
    if (sentryInitialized) {
        return;
    }

    sentryInitialized = true;
    const dsn = getSentryDsn();
    const release = getSentryRelease();

    safeRun('init', () => {
        Sentry.init({
            dsn,
            enabled: Boolean(dsn),
            sendDefaultPii: true,
            environment: import.meta.env.MODE,
            release,
            enableLogs: true,
            integrations: (defaults) => [
                ...defaults.map(preserveDiagnosticOccurrences),
                Sentry.reactRouterV6BrowserTracingIntegration({
                    useEffect: React.useEffect,
                    useLocation,
                    useNavigationType,
                    createRoutesFromChildren,
                    matchRoutes,
                }),
                Sentry.httpClientIntegration({
                    failedRequestStatusCodes: SENTRY_FAILED_REQUEST_STATUS_CODES,
                    failedRequestTargets: [/\/api\//],
                }),
                Sentry.captureConsoleIntegration({ levels: ['error'] }),
            ],
            tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
            beforeSend(event) {
                event.tags = {
                    ...event.tags,
                    app: 'website',
                };
                return redactShopifyInstallTelemetry(event);
            },
            beforeBreadcrumb: (breadcrumb) => redactShopifyInstallTelemetry(breadcrumb),
            beforeSendTransaction: (event) => redactShopifyInstallTelemetry(event),
            beforeSendLog(log) {
                log.attributes = normalizeAttributes(log.attributes || {});
                return redactShopifyInstallTelemetry(log);
            },
        });

        (globalThis as typeof globalThis & { Sentry?: typeof Sentry }).Sentry = Sentry;
    }, undefined);

    setApiSentryReporter((error, context) => {
        if (!shouldReportApiErrorToSentry(context)) {
            return;
        }

        captureSentryError(error, {
            area: 'api',
            api_kind: context.kind,
            api_method: context.method?.toUpperCase(),
            api_url: sanitizeUrl(context.url),
            api_base_url: sanitizeUrl(context.baseURL),
            api_status: context.status,
            api_code: context.code,
            api_message: context.message,
            workspace_id: context.workspaceId,
        });
    });

    setApiTimingReporter((report) => {
        const attributes = buildApiTimingAttributes(report);
        const shouldWarn = report.durationMs > SLOW_API_THRESHOLD_MS
            || report.timeout
            || report.aborted
            || report.status === 429
            || (report.status != null && report.status >= 500);
        logSentryEvent('rum.api_timing', attributes, shouldWarn ? 'warn' : 'info');

        if (shouldWarn) {
            logSentryEvent('rum.slow_trace', {
                ...attributes,
                event: 'slow_trace',
                'trace.reason': report.timeout
                    ? 'timeout'
                    : report.aborted
                        ? 'abort'
                        : report.status === 429
                            ? 'rate_limited'
                            : report.status != null && report.status >= 500
                                ? 'server_error'
                                : 'slow_api',
            }, 'warn');
        }
    });
};

export const wrapCreateBrowserRouter = <T extends (...args: any[]) => any>(createBrowserRouter: T): T => (
    safeRun(
        'wrap router',
        () => Sentry.wrapCreateBrowserRouterV6(createBrowserRouter) as T,
        createBrowserRouter,
    )
);

export const logSentryEvent = (
    event: string,
    attributes?: Record<string, unknown>,
    level: LogLevel = 'info',
) => {
    safeRun('log event', () => {
        const normalizedAttributes = normalizeAttributes(attributes);
        const sentryLogger = Sentry.logger as Partial<Record<LogLevel, (message: string, attributes?: Record<string, unknown>) => void>> | undefined;
        const logger = sentryLogger?.[level] || sentryLogger?.info;

        logger?.(event, normalizedAttributes);
    }, undefined);
};

/**
 * Sends a small set of investigation-critical lifecycle events through the
 * Sentry event pipeline as well as Logs. Unlike Logs Explorer results, these
 * events can be inspected separately from sampled log queries. Both pipelines
 * remain subject to transport failures, Sentry quotas and server-side filters.
 */
export const captureSentryDiagnosticEvent = (
    event: string,
    attributes: Record<string, unknown> = {},
    level: 'info' | 'warn' = 'info',
) => {
    safeRun('capture diagnostic event', () => {
        const normalizedAttributes = normalizeAttributes(attributes);
        const area = typeof normalizedAttributes.area === 'string'
            ? normalizedAttributes.area
            : 'frontend';

        Sentry.withScope((scope) => {
            scope.setLevel(level === 'warn' ? 'warning' : 'info');
            scope.setTag('app', 'website');
            scope.setTag('area', area);
            scope.setTag('diagnostic_event', event);
            scope.setFingerprint(['frontend-diagnostic', event]);
            scope.setContext('bustly', normalizedAttributes);
            Sentry.captureMessage(event);
        });
    }, undefined);
};

export const captureSentryError = (
    error: unknown,
    context: Record<string, unknown> = {},
) => {
    safeRun('capture error', () => {
        const normalizedContext = normalizeAttributes(context);
        const normalizedError = normalizeError(error);

        Sentry.withScope((scope) => {
            const area = typeof normalizedContext.area === 'string' ? normalizedContext.area : 'frontend';
            scope.setTag('app', 'website');
            scope.setTag('area', area);

            for (const [key, value] of Object.entries(normalizedContext)) {
                if (
                    value == null
                    || typeof value === 'object'
                    || key === 'route_search'
                ) {
                    continue;
                }
                scope.setTag(key, String(value).slice(0, 200));
            }

            scope.setContext('bustly', normalizedContext);
            Sentry.captureException(normalizedError);
        });
    }, undefined);
};

export const setSentryUserContext = (context: SentryUserContext | null) => {
    safeRun('set user context', () => {
        if (!context) {
            Sentry.setUser(null);
            Sentry.setContext('workspace', null);
            Sentry.setTag('workspace_id', 'none');
            return;
        }

        Sentry.setUser({
            id: context.id || undefined,
            email: context.email || undefined,
            username: context.username || undefined,
        });
        Sentry.setContext('workspace', {
            id: context.workspaceId || null,
            role: context.workspaceRole || null,
        });
        Sentry.setTag('workspace_id', context.workspaceId || 'none');
    }, undefined);
};
